/**
 * RoomSidebar — member list, host badge, invite code, host-key claim, and chat.
 *
 * Purely presentational; membership is fed from RoomPage, which subscribes to
 * the socket room:state/room:members events. The invite code is shown only
 * when known (host-at-creation); otherwise the shareable room URL is offered.
 * The host key is shown to the host so they can share it with trusted viewers
 * who can then claim host using the "Claim host" form.
 */
import { useMemo, useState, useEffect, useRef, type FormEvent, type ChangeEvent } from 'react';
import type { Member, Role, RoomSummary, ChatMessagePayload, ChatErrorPayload, ChatCooldownPayload } from '@syncreel/shared';
import { Badge } from './ui/Badge';
import { IconButton } from './ui/IconButton';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import { getSocket } from '../socket/socket';
import { kickMember, blacklistMember, uploadSubtitle } from '../api/client';
import { nameColor } from '../utils/nameColor';
import { srtToVtt, readFileAsText, vttToBlobUrl } from '../utils/subtitles';
import styles from './RoomSidebar.module.css';

export interface RoomSidebarProps {
  /** Active room summary. */
  room: RoomSummary;
  /** Current members (from socket). */
  members: Member[];
  /** Caller's role. */
  role: Role;
  /** Invite code, known only to the host at creation; null for joiners. */
  joinCode: string | null;
  /** Host key, known only to the host at creation; null for joiners. */
  hostKey: string | null;
  /** Room ID for emitting socket events. */
  roomId: string;
  /**
   * Called when a viewer uploads a local subtitle file. The parent (RoomStage)
   * adds it to the player as a blob-URL text track. Hosts do not call this —
   * they upload to the server, which broadcasts to everyone.
   */
  onLocalSubtitle?: (label: string, blobUrl: string) => void;
  /**
   * When true, the sidebar is in fullscreen+chat mode — it floats as a
   * semi-transparent overlay on the right side of the video.
   */
  floating?: boolean;
  /**
   * When true (and floating), the sidebar is collapsed to a thin tab so the
   * video is unobstructed. Clicking the tab calls onToggleCollapse.
   */
  collapsed?: boolean;
  /** Toggle the collapsed state of the floating sidebar. */
  onToggleCollapse?: () => void;
}

/** Sort members by join time (earliest first), then by name for stability. */
function sortMembers(members: readonly Member[]): Member[] {
  return [...members].sort((a, b) => a.joinedAt - b.joinedAt || a.displayName.localeCompare(b.displayName));
}

/** Build the shareable room URL from the current location. */
function roomUrl(): string {
  // WHY: location is always available in the browser; no SSR here.
  return typeof window !== 'undefined' ? window.location.href : '';
}

/** Render the room sidebar with members, invite info, and chat. */
export function RoomSidebar({ room, members, role, joinCode, hostKey, roomId, onLocalSubtitle, floating, collapsed, onToggleCollapse }: RoomSidebarProps) {
  const toast = useToast();
  const sorted = useMemo(() => sortMembers(members), [members]);
  const inviteText = joinCode ?? roomUrl();
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimKey, setClaimKey] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [kickingId, setKickingId] = useState<string | null>(null);
  const [blacklistingId, setBlacklistingId] = useState<string | null>(null);
  const [mutingId, setMutingId] = useState<string | null>(null);
  const [mutedUserIds, setMutedUserIds] = useState<Set<string>>(new Set());
  const [chatMessages, setChatMessages] = useState<ChatMessagePayload[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const chatListRef = useRef<HTMLDivElement>(null);
  const [uploadingSub, setUploadingSub] = useState(false);
  const subFileRef = useRef<HTMLInputElement>(null);

  // WHY: subscribe to chat:message events and auto-scroll to bottom.
  useEffect(() => {
    const sock = getSocket(roomId);
    const onChatMessage = (p: ChatMessagePayload) => {
      setChatMessages((prev) => [...prev, p].slice(-100));
    };
    sock.on('chat:message', onChatMessage);
    // WHY: listen for chat:cooldown events so the client can show a countdown
    // and disable the input while the user is rate-limited.
    const onChatCooldown = (p: ChatCooldownPayload) => {
      setCooldownSeconds(p.remainingSeconds);
    };
    sock.on('chat:cooldown', onChatCooldown);
    return () => {
      sock.off('chat:message', onChatMessage);
      sock.off('chat:cooldown', onChatCooldown);
    };
  }, [roomId]);

  // WHY: subscribe to userMuted/userUnmuted so the host and all viewers see
  // the muted badge appear/disappear in real time. The mute is IP-based
  // server-side; the client tracks muted userIds for display purposes.
  useEffect(() => {
    const sock = getSocket(roomId);
    const onUserMuted = (p: { userId: string }) => {
      setMutedUserIds((prev) => {
        const next = new Set(prev);
        next.add(p.userId);
        return next;
      });
    };
    const onUserUnmuted = (p: { userId: string }) => {
      setMutedUserIds((prev) => {
        const next = new Set(prev);
        next.delete(p.userId);
        return next;
      });
    };
    const onChatError = (p: ChatErrorPayload) => {
      toast.error(p.message);
    };
    sock.on('userMuted', onUserMuted);
    sock.on('userUnmuted', onUserUnmuted);
    sock.on('chat:error', onChatError);
    return () => {
      sock.off('userMuted', onUserMuted);
      sock.off('userUnmuted', onUserUnmuted);
      sock.off('chat:error', onChatError);
    };
  }, [roomId, toast]);

  // WHY: auto-scroll to the latest message.
  useEffect(() => {
    const el = chatListRef.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, [chatMessages]);

  // WHY: tick down the cooldown counter once per second until it reaches 0.
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = setInterval(() => {
      setCooldownSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const handleChatSend = (e: FormEvent) => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text) return;
    // WHY: prevent sending while the rate-limit cooldown is active.
    if (cooldownSeconds > 0) return;
    const sock = getSocket(roomId);
    sock.emit('chat:send', { roomId, text });
    setChatInput('');
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      // WHY: navigator.clipboard is only available in secure contexts (HTTPS
      // or localhost). On plain HTTP (e.g. http://<server-ip>:8080), it's
      // undefined and throws. Fall back to the deprecated execCommand API.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('execCommand copy failed');
      }
      toast.success(`${label} copied.`);
    } catch {
      toast.error(`Couldn't copy ${label}.`);
    }
  };

  const handleKick = async (member: Member) => {
    setKickingId(member.userId);
    try {
      // WHY: use the socket for real-time kick so the viewer is disconnected
      // immediately; the REST call ensures the DB is updated even if the
      // socket is stale.
      const sock = getSocket(roomId);
      sock.emit('room:kick', { roomId, userId: member.userId });
      await kickMember(roomId, member.userId);
      toast.success(`Kicked ${member.displayName}.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to kick member.';
      toast.error(message);
    } finally {
      setKickingId(null);
    }
  };

  const handleBlacklist = async (member: Member) => {
    const ok = window.confirm(`Blacklist ${member.displayName}'s IP? They will be unable to rejoin.`);
    if (!ok) return;
    setBlacklistingId(member.userId);
    try {
      await blacklistMember(roomId, member.userId);
      // WHY: also emit a socket kick to disconnect them immediately.
      const sock = getSocket(roomId);
      sock.emit('room:kick', { roomId, userId: member.userId });
      toast.success(`Blacklisted ${member.displayName}.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to blacklist member.';
      toast.error(message);
    } finally {
      setBlacklistingId(null);
    }
  };

  const handleMuteToggle = (member: Member) => {
    const isMuted = mutedUserIds.has(member.userId);
    setMutingId(member.userId);
    try {
      const sock = getSocket(roomId);
      if (isMuted) {
        sock.emit('room:unmute', { roomId, userId: member.userId });
        toast.success(`Unmuted ${member.displayName}.`);
      } else {
        sock.emit('room:mute', { roomId, userId: member.userId });
        toast.success(`Muted ${member.displayName}.`);
      }
    } catch {
      toast.error('Failed to toggle mute.');
    } finally {
      setMutingId(null);
    }
  };

  const handleClaimHost = (e: FormEvent) => {
    e.preventDefault();
    const key = claimKey.trim();
    if (!key) {
      toast.error('Enter a host key.');
      return;
    }
    setClaiming(true);
    try {
      const sock = getSocket(roomId);
      sock.emit('room:claim-host', { roomId, hostKey: key });
      // WHY: the server responds via room:state (success) or error (failure);
      // RoomPage's socket listeners handle the state update and toast.
      setClaimOpen(false);
      setClaimKey('');
      toast.info('Claiming host…');
    } catch {
      toast.error('Failed to claim host.');
    } finally {
      setClaiming(false);
    }
  };

  const handleSubtitleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // eslint-disable-next-line no-console
    console.log('[subtitles] handleSubtitleFile fired', { hasFile: !!file, role, roomId });
    if (!file) return;
    // WHY: reset the input value so selecting the same file again fires onChange.
    e.target.value = '';
    const label = file.name.replace(/\.(srt|vtt)$/i, '').slice(0, 60) || 'Custom';
    try {
      if (role === 'host') {
        // WHY: host uploads go to the server, which stores the file and
        // broadcasts room:subtitle-added to everyone (including the host).
        setUploadingSub(true);
        const text = await readFileAsText(file);
        await uploadSubtitle(roomId, text, { label });
        toast.success(`Subtitle "${label}" uploaded — available to everyone.`);
      } else if (onLocalSubtitle) {
        // WHY: viewer uploads are local-only — converted to VTT in the browser
        // and added as a blob-URL track to just this viewer's player.
        const text = await readFileAsText(file);
        const vtt = srtToVtt(text);
        const url = vttToBlobUrl(vtt);
        onLocalSubtitle(label, url);
        toast.success(`Subtitle "${label}" added (local only).`);
      } else {
        toast.error('Local subtitles are not available.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add subtitle.';
      toast.error(message);
    } finally {
      setUploadingSub(false);
    }
  };

  const sidebarClass = [
    styles.sidebar,
    floating ? styles.floating : '',
    floating && collapsed ? styles.collapsed : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={sidebarClass} aria-label="Room members">
      {floating ? (
        <button
          type="button"
          className={styles.collapseTab}
          onClick={() => onToggleCollapse?.()}
          title={collapsed ? 'Show chat' : 'Hide chat'}
          aria-label={collapsed ? 'Show chat' : 'Hide chat'}
        >
          {collapsed ? '◀' : '▶'}
        </button>
      ) : null}
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <h2 className={styles.title} title={room.name}>
            {room.name}
          </h2>
        </div>
        <div className={styles.codeRow}>
          <span className={styles.codeLabel}>Invite:</span>
          <code className={styles.code}>{inviteText}</code>
          <IconButton label="Copy invite" onClick={() => handleCopy(inviteText, 'Invite')} tooltip="Copy invite">
            ⧉
          </IconButton>
        </div>
        {role === 'host' && hostKey ? (
          <div className={styles.codeRow}>
            <span className={styles.codeLabel}>Host key:</span>
            <code className={styles.code}>{hostKey}</code>
            <IconButton label="Copy host key" onClick={() => handleCopy(hostKey, 'Host key')} tooltip="Copy host key">
              ⧉
            </IconButton>
          </div>
        ) : null}
        <div className={styles.subRow}>
          <input
            ref={subFileRef}
            type="file"
            accept=".srt,.vtt,text/vtt,application/octet-stream"
            onChange={handleSubtitleFile}
            className={styles.subFileInput}
            aria-label="Upload subtitle file"
            disabled={uploadingSub}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={uploadingSub}
            disabled={uploadingSub}
            onClick={() => subFileRef.current?.click()}
            className={styles.subButton}
          >
            {role === 'host' ? 'Add subtitle (everyone)' : 'Add subtitle (local)'}
          </Button>
        </div>
      </header>

      <div className={styles.chat}>
        <div className={styles.chatHeader}>Chat</div>
        <div className={styles.chatMessages} ref={chatListRef}>
          {chatMessages.length === 0 ? (
            <div className={styles.chatEmpty}>No messages yet.</div>
          ) : (
            chatMessages.map((m, i) => (
              <div key={i} className={styles.chatMessage}>
                <span className={styles.chatAuthor} title={m.displayName} style={{ color: nameColor(m.displayName) }}>
                  {m.displayName}:
                </span>
                <span className={styles.chatText}>{m.text}</span>
              </div>
            ))
          )}
        </div>
        <form className={styles.chatForm} onSubmit={handleChatSend}>
          <input
            className={`input ${styles.chatInput}`}
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            maxLength={500}
            placeholder={cooldownSeconds > 0 ? `Cooldown: ${cooldownSeconds}s` : 'Type a message…'}
            autoComplete="off"
            aria-label="Chat message"
            disabled={cooldownSeconds > 0}
          />
          <Button type="submit" variant="primary" size="sm" disabled={!chatInput.trim() || cooldownSeconds > 0}>
            Send
          </Button>
        </form>
        {cooldownSeconds > 0 ? (
          <div
            style={{ padding: '4px 18px 8px', fontSize: 'var(--fs-xs)', color: 'var(--color-text-subtle)' }}
            role="status"
            aria-live="polite"
          >
            You&apos;re sending messages too fast. Try again in {cooldownSeconds}s.
          </div>
        ) : null}
      </div>

      {role !== 'host' ? (
        <div className={styles.claimHost}>
          {claimOpen ? (
            <form className={styles.claimForm} onSubmit={handleClaimHost}>
              <input
                className={`input ${styles.claimInput}`}
                value={claimKey}
                onChange={(e) => setClaimKey(e.target.value)}
                placeholder="Host key"
                maxLength={64}
                autoComplete="off"
                aria-label="Host key"
              />
              <div className={styles.claimButtons}>
                <Button type="submit" variant="primary" size="sm" loading={claiming} disabled={claiming}>
                  Claim
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setClaimOpen(false);
                    setClaimKey('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <Button variant="ghost" size="sm" className={styles.claimToggle} onClick={() => setClaimOpen(true)}>
              Claim host
            </Button>
          )}
        </div>
      ) : null}

      <div className={styles.membersHead}>
        <span className={styles.membersCount}>Members ({sorted.length})</span>
        {role === 'host' ? <Badge variant="info" label="You are host" /> : null}
      </div>

      <ul className={styles.members}>
        {sorted.length === 0 ? (
          <li className={styles.empty}>No members yet.</li>
        ) : (
          sorted.map((m) => {
            const isMuted = mutedUserIds.has(m.userId);
            return (
            <li key={m.userId} className={styles.member}>
              <span className={styles.memberName} title={m.displayName}>
                {m.displayName}
                {isMuted ? <span className={styles.mutedBadge} title="Muted">🔇</span> : null}
              </span>
              {m.role === 'host' ? <Badge variant="ok" label="Host" /> : null}
              {role === 'host' && m.role !== 'host' ? (
                <div className={styles.memberActions}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    loading={mutingId === m.userId}
                    disabled={kickingId !== null || blacklistingId !== null || mutingId !== null}
                    onClick={() => handleMuteToggle(m)}
                    title={isMuted ? 'Unmute' : 'Mute'}
                  >
                    {isMuted ? 'Unmute' : 'Mute'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    loading={kickingId === m.userId}
                    disabled={kickingId !== null || blacklistingId !== null || mutingId !== null}
                    onClick={() => handleKick(m)}
                  >
                    Kick
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    loading={blacklistingId === m.userId}
                    disabled={kickingId !== null || blacklistingId !== null || mutingId !== null}
                    onClick={() => handleBlacklist(m)}
                  >
                    Ban
                  </Button>
                </div>
              ) : null}
            </li>
            );
          })
        )}
      </ul>
    </aside>
  );
}
